import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'components/Notification'
import {
  fetchCustomerUploadInstructions,
  deleteCustomerUploadInstruction,
  downloadCustomerUploadInstruction,
} from 'services/customerUploadInstructions'

const useCustomerRequestInstructions = customer => {
  const queryClient = useQueryClient()
  const { createNotification } = useNotification()

  const { data: customerInstructions, isFetching: isFetchingCustomerInstructions } = useQuery({
    queryFn: () => fetchCustomerUploadInstructions(customer.id),
    queryKey: ['fetchCustomerInstructions', customer.id],
    enabled: !!customer?.id,
    initialData: [],
  })

  const { mutate: deleteCustomerInstruction } = useMutation({
    mutationFn: id => deleteCustomerUploadInstruction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchCustomerInstructions', customer.id] })
      createNotification({
        message: 'Removed customer instruction',
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        message: 'Unable to delete the customer instruction',
        variant: 'error',
      })
    },
  })

  // The download link generated from the BE expires after 5
  // minutes. The cached link in the FE is expired in 3 minutes
  // to give a little buffer room.
  const handleFileDownload = async (id, filePath) => {
    try {
      const { data } = await queryClient.fetchQuery(
        ['download', 'customerRequest', 'instruction', { id, filePath }],
        () => downloadCustomerUploadInstruction(id, filePath),
        {
          staleTime: 1000 * 60 * 3,
        },
      )
      window.open(data.fileUrl)
    } catch {
      createNotification({
        variant: 'error',
        message: 'Encountered an error while downloading the file. Please try again later',
      })
    }
  }

  return {
    customerInstructions,
    isFetchingCustomerInstructions,
    handleInstructionDelete: deleteCustomerInstruction,
    handleFileDownload,
  }
}

export default useCustomerRequestInstructions
